"use client";

import { cn } from "@/lib/utils";
import { CandlestickChart, Loader2 } from "lucide-react";

interface ChartNoSymbolSelectedContentProps {
  isDesktop?: boolean;
  /**
   * Proposal 74: this panel had a symbol last session and is waiting for the
   * symbol list to arrive before it can be restored. Shown instead of the
   * "pick something" empty state so a blank chart at startup explains itself.
   */
  restoring?: { symbol: string; timeframe: string } | null;
}

export function ChartNoSymbolSelectedContent({
  isDesktop = false,
  restoring = null,
}: ChartNoSymbolSelectedContentProps) {
  if (restoring) {
    return (
      <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-muted/25 via-background to-background"
          aria-hidden
        />
        <div className="relative z-10 flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
          <Loader2
            className="h-3.5 w-3.5 animate-spin text-muted-foreground"
            strokeWidth={2}
            aria-hidden
          />
          <span className="font-mono text-[11px] text-muted-foreground">
            Restoring {restoring.symbol} · {restoring.timeframe}…
          </span>
        </div>
      </div>
    );
  }


  return (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-muted/25 via-background to-background"
        aria-hidden
      />

      <div
        className={cn(
          "relative z-10 px-6 text-center",
          isDesktop ? "max-w-xs" : "max-w-sm",
        )}
      >
        <CandlestickChart
          className={cn(
            "mx-auto text-muted-foreground/60",
            isDesktop ? "mb-3 h-8 w-8" : "mb-4 h-10 w-10",
          )}
          strokeWidth={1.5}
        />
        <h3
          className={cn(
            "font-semibold text-foreground",
            isDesktop ? "text-sm" : "text-lg",
          )}
        >
          No symbol selected
        </h3>
        <p
          className={cn(
            "mt-1.5 text-muted-foreground",
            isDesktop ? "text-[11px] leading-relaxed" : "text-sm leading-relaxed",
          )}
        >
          Choose an instrument from the toolbar, or add symbols in Quotes.
        </p>
      </div>
    </div>
  );
}
